"use client"

import { Trash2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"
import { cn } from "@/lib/utils"
import {
  CHANNEL_MATCHER_FIELD_LABELS,
  SUPPORTED_MATCHER_FIELDS,
  SUPPORTED_MATCHER_OPERATORS,
  type ChannelMatcherDTO,
} from "@/types/channel-config"
import { useTranslations } from "next-intl"


interface MatcherRowProps {
  matcher: ChannelMatcherDTO
  /** Unique per row; prefixes the toggle ids. */
  idPrefix: string
  className?: string
  onChange: (key: "field" | "operator" | "value", value: string) => void
  onNegatedToggle: (checked: boolean) => void
  onAnyValueToggle: (checked: boolean) => void
  onRemove: () => void
}

export function MatcherRow({ matcher, idPrefix, className, onChange, onNegatedToggle, onAnyValueToggle, onRemove }: MatcherRowProps) {
  const tUi = useTranslations("Shopify.ShopifyAttributionDashboardDialogsMatcherRow")

  return (
    <div className={cn("items-top grid gap-2 rounded-md border p-3 md:grid-cols-[auto_1fr_1fr_2fr_auto_auto] md:items-center", className)}>
      <div className="flex items-center gap-2">
        <Switch id={`${idPrefix}-not`} checked={Boolean(matcher.isNegated)} onCheckedChange={onNegatedToggle} />
        <Label htmlFor={`${idPrefix}-not`} className="text-xs font-bold text-destructive">
          <span key={tUi("nOT")}>{tUi("nOT")}</span></Label>
      </div>
      <Select value={matcher.field} onValueChange={(value) => onChange("field", value)}>
        <SelectTrigger>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {SUPPORTED_MATCHER_FIELDS.map((field) => (
            <SelectItem key={field} value={field}>
              {CHANNEL_MATCHER_FIELD_LABELS[field]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select value={matcher.operator} onValueChange={(value) => onChange("operator", value)}>
        <SelectTrigger>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {SUPPORTED_MATCHER_OPERATORS.map((operator) => (
            <SelectItem key={operator} value={operator}>
              {operator}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Input
        value={matcher.value}
        placeholder={matcher.isAnyValue ? tUi("anyValue") : tUi("value")}
        disabled={Boolean(matcher.isAnyValue)}
        onChange={(event) => onChange("value", event.target.value)}
      />
      <div className="flex items-center gap-2">
        <Switch id={`${idPrefix}-any`} checked={Boolean(matcher.isAnyValue)} onCheckedChange={onAnyValueToggle} />
        <Label htmlFor={`${idPrefix}-any`} className="text-xs font-normal text-muted-foreground">
          <span key={tUi("anyValue")}>{tUi("anyValue")}</span></Label>
      </div>
      <Button size="icon" variant="ghost" onClick={onRemove} aria-label={tUi("removeMatcher")}>
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
  )
}
